<template>
	<div class="max-w-6xl mx-auto p-4 space-y-6">
		<h1 class="text-lg font-medium">API Comparison: GQL vs JSON</h1>
		<p class="text-sm text-muted-foreground">
			Compare results from the new GraphQL endpoints against the legacy JSON service endpoints.
		</p>

		<!-- Controls -->
		<div class="grid grid-cols-1 md:grid-cols-5 gap-3">
			<div>
				<Label class="text-xs">Vhost ID</Label>
				<Input v-model="testVhostId" type="number" class="mt-1" />
			</div>
			<div>
				<Label class="text-xs">Search Query</Label>
				<Input v-model="testQuery" placeholder="Min 4 chars..." class="mt-1" />
			</div>
			<div>
				<Label class="text-xs">Parent ID (optional)</Label>
				<Input v-model="testParentId" class="mt-1" />
			</div>
			<div>
				<Label class="text-xs">Test Filter</Label>
				<select v-model="testFilter" class="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
					<option value="all">All Tests</option>
					<option value="meta">Custom Meta Only</option>
					<option value="project">Project Info Only</option>
					<option value="search">Search Only</option>
				</select>
			</div>
			<div class="flex items-end">
				<Button @click="runAllTests" :disabled="running" class="w-full">
					{{ running ? 'Running...' : 'Run Tests' }}
				</Button>
			</div>
		</div>

		<!-- Summary Bar -->
		<div v-if="testResults.length > 0" class="flex items-center gap-4 text-sm">
			<span class="text-green-600 font-medium">{{ passCount }} pass</span>
			<span class="text-amber-600 font-medium">{{ warnCount }} warn</span>
			<span class="text-red-600 font-medium">{{ failCount }} fail</span>
			<span class="text-muted-foreground">{{ pendingCount }} pending</span>
			<span class="ml-auto text-xs text-muted-foreground">
				Total GQL: {{ totalGqlTime }}ms | Total JSON: {{ totalJsonTime }}ms
				<span v-if="totalGqlTime > 0 && totalJsonTime > 0" class="ml-1">
					({{ totalGqlTime < totalJsonTime ? 'GQL faster' : 'JSON faster' }} by {{ Math.abs(totalGqlTime - totalJsonTime) }}ms)
				</span>
			</span>
		</div>

		<!-- Test Results -->
		<div v-for="(test, idx) in testResults" :key="idx" class="border rounded-lg overflow-hidden">
			<div class="flex items-center justify-between px-4 py-2 bg-muted/50 border-b cursor-pointer" @click="test.collapsed = !test.collapsed">
				<div class="flex items-center gap-2">
					<span class="text-xs text-muted-foreground w-5">{{ idx + 1 }}</span>
					<span class="text-sm font-medium">{{ test.name }}</span>
					<span v-if="test.status === 'pass'" class="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">PASS</span>
					<span v-else-if="test.status === 'fail'" class="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">FAIL</span>
					<span v-else-if="test.status === 'warn'" class="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">WARN</span>
					<span v-else-if="test.status === 'skip'" class="text-xs text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full">SKIP</span>
					<span v-else class="text-xs text-muted-foreground">pending</span>
				</div>
				<div class="flex items-center gap-3 text-xs text-muted-foreground">
					<span v-if="test.gqlTime !== null">GQL: {{ test.gqlTime }}ms</span>
					<span v-if="test.jsonTime !== null">JSON: {{ test.jsonTime }}ms</span>
					<span class="text-[10px]">{{ test.collapsed ? '▶' : '▼' }}</span>
				</div>
			</div>
			<template v-if="!test.collapsed">
				<div v-if="test.details" class="px-4 py-2 text-xs text-muted-foreground bg-muted/20">{{ test.details }}</div>
				<div class="grid grid-cols-2 divide-x">
					<div class="p-3">
						<p class="text-xs font-medium mb-1">GQL Response</p>
						<pre class="text-[11px] bg-muted/30 rounded p-2 overflow-auto max-h-60 whitespace-pre-wrap">{{ test.gqlData ? JSON.stringify(test.gqlData, null, 2) : '—' }}</pre>
					</div>
					<div class="p-3">
						<p class="text-xs font-medium mb-1">JSON Response</p>
						<pre class="text-[11px] bg-muted/30 rounded p-2 overflow-auto max-h-60 whitespace-pre-wrap">{{ test.jsonData ? JSON.stringify(test.jsonData, null, 2) : '—' }}</pre>
					</div>
				</div>
				<div v-if="test.diff" class="px-4 py-2 border-t bg-red-50/50">
					<p class="text-xs font-medium text-red-700 mb-1">Differences</p>
					<pre class="text-[11px] text-red-600 whitespace-pre-wrap">{{ test.diff }}</pre>
				</div>
				<div v-if="test.fieldReport" class="px-4 py-2 border-t bg-blue-50/50">
					<p class="text-xs font-medium text-blue-700 mb-1">Field Comparison</p>
					<pre class="text-[11px] text-blue-600 whitespace-pre-wrap">{{ test.fieldReport }}</pre>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, reactive, inject, computed, type Ref } from 'vue'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import vhostService from '@/services/Vhost'
import axios from 'axios'

interface TestResult {
	name: string
	status: 'pass' | 'fail' | 'warn' | 'pending' | 'skip'
	gqlData: any
	jsonData: any
	gqlTime: number | null
	jsonTime: number | null
	details: string
	diff: string
	fieldReport: string
	collapsed: boolean
}

const vhostId = inject<Ref<string> | null>('vhostId', null)
const { getCustomMeta, getCustomMetaProviders, getCustomMetaGroupings, search3rdPartyMeta, getVhostInfo, getTabs } = vhostService()

const testVhostId = ref(vhostId?.value || '')
const testQuery = ref('')
const testParentId = ref('')
const testFilter = ref('all')
const running = ref(false)
const testResults = reactive<TestResult[]>([])

const passCount = computed(() => testResults.filter(t => t.status === 'pass').length)
const warnCount = computed(() => testResults.filter(t => t.status === 'warn').length)
const failCount = computed(() => testResults.filter(t => t.status === 'fail').length)
const pendingCount = computed(() => testResults.filter(t => t.status === 'pending').length)
const totalGqlTime = computed(() => testResults.reduce((s, t) => s + (t.gqlTime || 0), 0))
const totalJsonTime = computed(() => testResults.reduce((s, t) => s + (t.jsonTime || 0), 0))

function makeResult(name: string): TestResult {
	return { name, status: 'pending', gqlData: null, jsonData: null, gqlTime: null, jsonTime: null, details: '', diff: '', fieldReport: '', collapsed: true }
}

function deepCompare(gql: any, json: any, path = ''): string[] {
	const diffs: string[] = []
	if (gql === json) return diffs
	if (gql === null || json === null || typeof gql !== typeof json) {
		diffs.push(`${path || 'root'}: type mismatch — GQL=${typeof gql}(${String(gql)?.substring(0, 80)}) vs JSON=${typeof json}(${String(json)?.substring(0, 80)})`)
		return diffs
	}
	if (Array.isArray(gql) && Array.isArray(json)) {
		if (gql.length !== json.length) {
			diffs.push(`${path || 'root'}: array length GQL=${gql.length} vs JSON=${json.length}`)
		}
		const max = Math.min(3, gql.length, json.length)
		for (let i = 0; i < max; i++) {
			diffs.push(...deepCompare(gql[i], json[i], `${path}[${i}]`))
		}
		return diffs
	}
	if (typeof gql === 'object') {
		const gqlKeys = Object.keys(gql).sort()
		const jsonKeys = Object.keys(json).sort()
		const allKeys = new Set([...gqlKeys, ...jsonKeys])
		const gqlOnly = gqlKeys.filter(k => !jsonKeys.includes(k))
		const jsonOnly = jsonKeys.filter(k => !gqlKeys.includes(k))
		if (gqlOnly.length > 0) diffs.push(`${path || 'root'}: GQL-only keys: [${gqlOnly.join(', ')}]`)
		if (jsonOnly.length > 0) diffs.push(`${path || 'root'}: JSON-only keys: [${jsonOnly.join(', ')}]`)
		for (const k of allKeys) {
			if (k in gql && k in json) {
				const gVal = String(gql[k])
				const jVal = String(json[k])
				if (gVal !== jVal && diffs.length < 20) {
					diffs.push(`${path}.${k}: GQL=${gVal.substring(0, 100)} vs JSON=${jVal.substring(0, 100)}`)
				}
			}
		}
		return diffs
	}
	if (String(gql) !== String(json)) {
		diffs.push(`${path || 'root'}: GQL=${String(gql).substring(0, 100)} vs JSON=${String(json).substring(0, 100)}`)
	}
	return diffs
}

function compareResults(gql: any, json: any): { match: boolean; diff: string; fieldReport: string } {
	const gqlStr = JSON.stringify(gql, null, 2)
	const jsonStr = JSON.stringify(json, null, 2)
	if (gqlStr === jsonStr) return { match: true, diff: '', fieldReport: '' }

	const diffs = deepCompare(gql, json)
	const fieldReport = generateFieldReport(gql, json)
	return {
		match: false,
		diff: diffs.length > 0 ? diffs.join('\n') : 'Data structures differ (see side-by-side)',
		fieldReport
	}
}

function generateFieldReport(gql: any, json: any): string {
	if (!Array.isArray(gql) || !Array.isArray(json) || gql.length === 0 || json.length === 0) return ''
	const gqlKeys = Object.keys(gql[0] || {}).sort()
	const jsonKeys = Object.keys(json[0] || {}).sort()
	if (JSON.stringify(gqlKeys) === JSON.stringify(jsonKeys)) return `Fields match: [${gqlKeys.join(', ')}]`

	const shared = gqlKeys.filter(k => jsonKeys.includes(k))
	const gqlOnly = gqlKeys.filter(k => !jsonKeys.includes(k))
	const jsonOnly = jsonKeys.filter(k => !gqlKeys.includes(k))
	const lines = [`Shared (${shared.length}): [${shared.join(', ')}]`]
	if (gqlOnly.length) lines.push(`GQL-only (${gqlOnly.length}): [${gqlOnly.join(', ')}]`)
	if (jsonOnly.length) lines.push(`JSON-only (${jsonOnly.length}): [${jsonOnly.join(', ')}]`)
	return lines.join('\n')
}

const runAllTests = async () => {
	const vid = parseInt(testVhostId.value)
	if (!vid) return

	running.value = true
	testResults.length = 0
	const shouldRun = (group: string) => testFilter.value === 'all' || testFilter.value === group

	// ─── Test 1: getProjectInfo ───
	if (shouldRun('project')) {
		const t = makeResult('getProjectInfo')
		testResults.push(t)
		try {
			const gqlStart = performance.now()
			t.gqlData = await getVhostInfo(String(vid))
			t.gqlTime = Math.round(performance.now() - gqlStart)

			const jsonStart = performance.now()
			const jsonRes = await axios.post('/services/json', { method: 'vhost.getProjectInfo', vhostID: vid })
			t.jsonData = jsonRes.data?.result || jsonRes.data?.data || jsonRes.data
			t.jsonTime = Math.round(performance.now() - jsonStart)

			const cmp = compareResults(t.gqlData, t.jsonData)
			t.status = cmp.match ? 'pass' : 'warn'
			t.diff = cmp.diff
			t.fieldReport = cmp.fieldReport
			t.details = `GQL returned project "${t.gqlData?.name || '?'}", JSON returned project "${t.jsonData?.name || '?'}"`
		} catch (err: any) {
			t.status = 'fail'
			t.details = err.message
		}
	}

	// ─── Test 2: getTabs ───
	if (shouldRun('project')) {
		const t = makeResult('getTabs')
		testResults.push(t)
		try {
			const gqlStart = performance.now()
			t.gqlData = await getTabs(vid)
			t.gqlTime = Math.round(performance.now() - gqlStart)

			const jsonStart = performance.now()
			const jsonRes = await axios.post('/services/json', { method: 'vhost.getTabs', vhostID: vid })
			t.jsonData = jsonRes.data?.result || jsonRes.data?.data || jsonRes.data
			t.jsonTime = Math.round(performance.now() - jsonStart)

			const cmp = compareResults(t.gqlData, t.jsonData)
			t.status = cmp.match ? 'pass' : 'warn'
			t.diff = cmp.diff
			t.fieldReport = cmp.fieldReport
			t.details = `GQL returned ${Array.isArray(t.gqlData) ? t.gqlData.length : '?'} tabs, JSON returned ${Array.isArray(t.jsonData) ? t.jsonData.length : '?'} tabs`
		} catch (err: any) {
			t.status = 'fail'
			t.details = err.message
		}
	}

	// ─── Test 3: getCustomMetaProviders ───
	if (shouldRun('meta')) {
		const t = makeResult('getCustomMetaProviders')
		testResults.push(t)
		try {
			const gqlStart = performance.now()
			t.gqlData = await getCustomMetaProviders(vid)
			t.gqlTime = Math.round(performance.now() - gqlStart)

			const jsonStart = performance.now()
			const jsonRes = await axios.post('/services/json', { method: 'vhost.getCustomMetaProviders', vhostID: vid })
			t.jsonData = jsonRes.data?.result || jsonRes.data?.data || jsonRes.data
			t.jsonTime = Math.round(performance.now() - jsonStart)

			const cmp = compareResults(t.gqlData, t.jsonData)
			t.status = cmp.match ? 'pass' : 'warn'
			t.diff = cmp.diff
			t.fieldReport = cmp.fieldReport
			t.details = `GQL returned ${Array.isArray(t.gqlData) ? t.gqlData.length : '?'} providers, JSON returned ${Array.isArray(t.jsonData) ? t.jsonData.length : '?'} providers`
		} catch (err: any) {
			t.status = 'fail'
			t.details = err.message
		}
	}

	// ─── Test 4: getCustomMeta ───
	if (shouldRun('meta')) {
		const t = makeResult('getCustomMeta')
		testResults.push(t)
		try {
			const gqlStart = performance.now()
			t.gqlData = await getCustomMeta(vid)
			t.gqlTime = Math.round(performance.now() - gqlStart)

			const jsonStart = performance.now()
			const jsonRes = await axios.post('/services/json', { method: 'vhost.getCustomMeta', vhostID: vid })
			t.jsonData = jsonRes.data?.result || jsonRes.data?.data || jsonRes.data
			t.jsonTime = Math.round(performance.now() - jsonStart)

			const cmp = compareResults(t.gqlData, t.jsonData)
			t.status = cmp.match ? 'pass' : 'warn'
			t.diff = cmp.diff
			t.fieldReport = cmp.fieldReport
			t.details = `GQL returned ${Array.isArray(t.gqlData) ? t.gqlData.length : '?'} fields, JSON returned ${Array.isArray(t.jsonData) ? t.jsonData.length : '?'} fields`
		} catch (err: any) {
			t.status = 'fail'
			t.details = err.message
		}
	}

	// ─── Test 5: getCustomMetaGroupings ───
	if (shouldRun('meta')) {
		const t = makeResult('getCustomMetaGroupings')
		testResults.push(t)
		try {
			const gqlStart = performance.now()
			t.gqlData = await getCustomMetaGroupings(vid)
			t.gqlTime = Math.round(performance.now() - gqlStart)

			const jsonStart = performance.now()
			const jsonRes = await axios.post('/services/json', { method: 'vhost.getCustomMetaGroupings', vhostID: vid })
			t.jsonData = jsonRes.data?.result || jsonRes.data?.data || jsonRes.data
			t.jsonTime = Math.round(performance.now() - jsonStart)

			const cmp = compareResults(t.gqlData, t.jsonData)
			t.status = cmp.match ? 'pass' : 'warn'
			t.diff = cmp.diff
			t.fieldReport = cmp.fieldReport
			t.details = `GQL returned ${Array.isArray(t.gqlData) ? t.gqlData.length : '?'} groupings, JSON returned ${Array.isArray(t.jsonData) ? t.jsonData.length : '?'} groupings`
		} catch (err: any) {
			t.status = 'fail'
			t.details = err.message
		}
	}

	// ─── Test 6: search3rdPartyMeta (only if query provided) ───
	if (shouldRun('search')) {
		if (testQuery.value && testQuery.value.length >= 4) {
			const t = makeResult('search3rdPartyMeta')
			testResults.push(t)
			try {
				const gqlStart = performance.now()
				t.gqlData = await search3rdPartyMeta(vid, testQuery.value, testParentId.value)
				t.gqlTime = Math.round(performance.now() - gqlStart)

				const jsonStart = performance.now()
				const jsonRes = await axios.get(`/services/json?method=vhost.get3rdPartyMetaData&vhostID=${vid}&query=${encodeURIComponent(testQuery.value)}&parentId=${encodeURIComponent(testParentId.value)}`)
				t.jsonData = jsonRes.data?.result || jsonRes.data?.data || jsonRes.data
				t.jsonTime = Math.round(performance.now() - jsonStart)

				const cmp = compareResults(t.gqlData, t.jsonData)
				t.status = cmp.match ? 'pass' : 'warn'
				t.diff = cmp.diff
				t.fieldReport = cmp.fieldReport

				const countNodes = (nodes: any[]): number => {
					let c = nodes.length
					for (const n of nodes) {
						if (n.children && Array.isArray(n.children)) c += countNodes(n.children)
					}
					return c
				}
				const gqlCount = Array.isArray(t.gqlData) ? countNodes(t.gqlData) : 0
				const jsonCount = Array.isArray(t.jsonData) ? countNodes(t.jsonData) : 0
				t.details = `Query "${testQuery.value}" — GQL: ${gqlCount} nodes, JSON: ${jsonCount} nodes`
			} catch (err: any) {
				t.status = 'fail'
				t.details = err.message
			}
		} else {
			const t = makeResult('search3rdPartyMeta')
			t.status = 'skip'
			t.details = 'Skipped — enter a search query with 4+ characters'
			testResults.push(t)
		}
	}

	// ─── Test 7: options field parsing ───
	if (shouldRun('meta')) {
		const t = makeResult('Options field parsing (JSON string vs array)')
		testResults.push(t)
		try {
			const existingMeta = testResults.find(r => r.name === 'getCustomMeta')
			const fields = existingMeta?.gqlData || await getCustomMeta(vid)
			const fieldsWithOptions = (fields as any[]).filter((f: any) => f.options)
			if (fieldsWithOptions.length > 0) {
				const sample = fieldsWithOptions.slice(0, 5)
				t.gqlData = sample.map((f: any) => ({
					key: f.key,
					label: f.label,
					optionsRaw: f.options,
					optionsType: typeof f.options,
					optionsIsArray: Array.isArray(f.options),
					parsedLength: (() => {
						try {
							const parsed = typeof f.options === 'string' ? JSON.parse(f.options) : f.options
							return Array.isArray(parsed) ? parsed.length : '?'
						} catch { return 'parse-error' }
					})(),
				}))

				const jsonFields = existingMeta?.jsonData
				const jsonWithOptions = Array.isArray(jsonFields)
					? jsonFields.filter((f: any) => f.options).slice(0, 5)
					: []
				t.jsonData = jsonWithOptions.map((f: any) => ({
					key: f.key,
					label: f.label,
					optionsRaw: f.options,
					optionsType: typeof f.options,
					optionsIsArray: Array.isArray(f.options),
					parsedLength: (() => {
						try {
							const parsed = typeof f.options === 'string' ? JSON.parse(f.options) : f.options
							return Array.isArray(parsed) ? parsed.length : '?'
						} catch { return 'parse-error' }
					})(),
				}))

				const gqlStrings = (t.gqlData as any[]).filter((f: any) => f.optionsType === 'string').length
				const jsonStrings = (t.jsonData as any[]).filter((f: any) => f.optionsType === 'string').length
				if (gqlStrings > 0) {
					t.status = 'warn'
					t.details = `${gqlStrings}/${t.gqlData.length} GQL fields have options as JSON strings (needs parseOptions). ${jsonStrings}/${t.jsonData.length} JSON fields have string options.`
				} else {
					t.status = 'pass'
					t.details = 'All options are already arrays — no parsing needed'
				}
			} else {
				t.status = 'pass'
				t.details = 'No fields with options found'
			}
		} catch (err: any) {
			t.status = 'fail'
			t.details = err.message
		}
	}

	// ─── Test 8: getPermissionGroups ───
	if (shouldRun('project')) {
		const t = makeResult('getPermissionGroups')
		testResults.push(t)
		try {
			const gqlStart = performance.now()
			const gqlQuery = `query ($vhost: Int!) { Vhost { getPermissionGroups(vhost: $vhost) { id name permissions } } }`
			const gqlRes = await axios.post('/gql', { query: gqlQuery, variables: { vhost: vid } })
			t.gqlData = gqlRes.data?.data?.Vhost?.getPermissionGroups || []
			t.gqlTime = Math.round(performance.now() - gqlStart)

			const jsonStart = performance.now()
			const jsonRes = await axios.post('/services/json', { method: 'vhost.getPermissionGroups', vhostID: vid })
			t.jsonData = jsonRes.data?.result || jsonRes.data?.data || jsonRes.data
			t.jsonTime = Math.round(performance.now() - jsonStart)

			const cmp = compareResults(t.gqlData, t.jsonData)
			t.status = cmp.match ? 'pass' : 'warn'
			t.diff = cmp.diff
			t.fieldReport = cmp.fieldReport
			t.details = `GQL returned ${Array.isArray(t.gqlData) ? t.gqlData.length : '?'} groups, JSON returned ${Array.isArray(t.jsonData) ? t.jsonData.length : '?'} groups`
		} catch (err: any) {
			t.status = 'fail'
			t.details = err.message
		}
	}

	// ─── Test 9: getTotals ───
	if (shouldRun('project')) {
		const t = makeResult('getTotals')
		testResults.push(t)
		try {
			const gqlStart = performance.now()
			const gqlQuery = `query { Vhost { getTotals(vhost: ${vid}, type: "all", period: "this month") {
				totalContributorsUnderLimit contributorLimit totalContributors
				adminLimit totalAdminUsers totalFilesize transferCount
				totalAISeconds aiMinuteLimit aiSecondLimit totalAISecondsUnderLimit
			} } }`
			const gqlRes = await axios.post('/gql', { query: gqlQuery })
			t.gqlData = gqlRes.data?.data?.Vhost?.getTotals
			t.gqlTime = Math.round(performance.now() - gqlStart)

			const jsonStart = performance.now()
			const jsonRes = await axios.post('/services/json', { method: 'vhost.getTotals', vhostID: vid, type: 'all', period: 'this month' })
			t.jsonData = jsonRes.data?.result || jsonRes.data?.data || jsonRes.data
			t.jsonTime = Math.round(performance.now() - jsonStart)

			const cmp = compareResults(t.gqlData, t.jsonData)
			t.status = cmp.match ? 'pass' : 'warn'
			t.diff = cmp.diff
			t.fieldReport = cmp.fieldReport
			t.details = `Quota totals — contributors: ${t.gqlData?.totalContributors || '?'}, filesize: ${t.gqlData?.totalFilesize || '?'}`
		} catch (err: any) {
			t.status = 'fail'
			t.details = err.message
		}
	}

	// Auto-expand first failed/warn test
	const firstIssue = testResults.find(t => t.status === 'fail' || t.status === 'warn')
	if (firstIssue) firstIssue.collapsed = false

	running.value = false
}
</script>
